import { LocalAuthHandler } from '@/utils/LocalAuthHandler';

/** JWT claim that identifies the authenticated Authvia user (new user tokens). */
export const AUTHVIA_USER_ID_CLAIM: 'https://authvia.com/user' =
  'https://authvia.com/user';

/**
 * Legacy JWT claim for user id. Kept only inside this module as a fallback so
 * existing `customer` / uuid tokens keep working during the user-token migration.
 */
export const AUTHVIA_USER_UUID_CLAIM: 'https://authvia.com/uuid' =
  'https://authvia.com/uuid';

export const AUTHVIA_ROLE_CLAIM: 'https://authvia.com/role' =
  'https://authvia.com/role';

/** Roles lock accepts for an authenticated session during the user-token migration. */
export const LOCK_ACCEPTED_ROLES: readonly string[] = ['user', 'customer'];

function asNonEmptyString(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value : '';
}

/**
 * Resolve authenticated user id from a decoded JWT payload.
 * Prefer `https://authvia.com/user`; fall back to legacy `https://authvia.com/uuid`.
 * Claim keys must not be read outside this module.
 */
export function getAuthenticatedUserId(
  payload: Record<string, unknown> | undefined | null,
): string {
  if (!payload) {
    return '';
  }

  return (
    asNonEmptyString(payload[AUTHVIA_USER_ID_CLAIM]) ||
    asNonEmptyString(payload[AUTHVIA_USER_UUID_CLAIM])
  );
}

export function getAuthenticatedUserIdFromToken(
  parseToken: (token: string) => Record<string, unknown>,
  token: string,
): string {
  if (!token) {
    return '';
  }

  try {
    return getAuthenticatedUserId(parseToken(token));
  } catch {
    return '';
  }
}

export function getAuthenticatedUserRole(
  payload: Record<string, unknown> | undefined | null,
): string {
  return asNonEmptyString(payload?.[AUTHVIA_ROLE_CLAIM]);
}

export function isLockAcceptedRole(role: unknown): boolean {
  return typeof role === 'string' && LOCK_ACCEPTED_ROLES.includes(role);
}

/**
 * USER-token API paths (no `/v3`, nested under `/users/...` where mapped).
 * Legacy `customer` tokens keep Current `/v3/...` shapes.
 */
export function isUserTokenApiPaths(
  payload: Record<string, unknown> | undefined | null,
): boolean {
  return getAuthenticatedUserRole(payload) === 'user';
}

export function isUserTokenApiPathsFromToken(
  parseToken: (token: string) => Record<string, unknown>,
  token: string,
): boolean {
  if (!token) {
    return false;
  }

  try {
    return isUserTokenApiPaths(parseToken(token));
  } catch {
    return false;
  }
}

/**
 * Pinia `apiPathPrefix` for the session: empty for USER tokens, `/v3` for legacy.
 */
export function getPiniaApiPathPrefixFromToken(
  parseToken: (token: string) => Record<string, unknown>,
  token: string,
): '' | '/v3' {
  return isUserTokenApiPathsFromToken(parseToken, token) ? '' : '/v3';
}

/**
 * When lock has no stored address, hydrate from JWT `sub` (email/phone on new
 * user tokens). Does not overwrite an existing OTP / query address. Never use
 * `sub` as `/users/{...}` path segment.
 */
export function syncLockAddressFromTokenPayload(
  payload: Record<string, unknown> | undefined | null,
): void {
  if (LocalAuthHandler.getLockAddress()) {
    return;
  }

  const sub: string = asNonEmptyString(payload?.sub);
  if (!sub) {
    return;
  }

  // Skip opaque subject values that are not contact addresses.
  const looksLikeEmail: boolean = sub.includes('@');
  const looksLikePhone: boolean = /\d{7,}/.test(sub.replace(/\D/g, ''));
  if (!looksLikeEmail && !looksLikePhone) {
    return;
  }

  LocalAuthHandler.setLockAddressFromValue(sub);
}

export function syncLockAddressFromToken(
  parseToken: (token: string) => Record<string, unknown>,
  token: string,
): void {
  if (!token) {
    return;
  }

  try {
    syncLockAddressFromTokenPayload(parseToken(token));
  } catch {
    // ignore malformed tokens
  }
}
